import { getFunctions, httpsCallable } from 'firebase/functions';

// Custom Claims Service for managing user roles via Firebase Auth Custom Claims
class CustomClaimsService {
  constructor(firebaseApp) {
    this.functions = getFunctions(firebaseApp);
    this.refreshUserClaims = httpsCallable(this.functions, 'refreshUserClaims');
    this.setUserClaims = httpsCallable(this.functions, 'setUserClaims');
  }

  /**
     * Refresh custom claims for the current authenticated user
     * This should be called after login to ensure the user has the latest role/team information
     */
  async refreshCurrentUserClaims() {
    try {
      console.log('🔄 Refreshing custom claims for current user...');
      const result = await this.refreshUserClaims();
      console.log('✅ Custom claims refreshed:', result.data.claims);
      return result.data;
    } catch (error) {
      console.error('❌ Error refreshing custom claims:', error);
      throw error;
    }
  }

  /**
     * Set custom claims for a specific user (admin only)
     * @param {string} email - User email to set claims for
     */
  async setClaimsForUser(email) {
    try {
      console.log(`🔄 Setting custom claims for user: ${email}`);
      const result = await this.setUserClaims({ email });
      console.log('✅ Custom claims set:', result.data);
      return result.data;
    } catch (error) {
      console.error(`❌ Error setting custom claims for ${email}:`, error);
      throw error;
    }
  }

  /**
     * Wait for custom claims to be applied to the current user's token
     * This forces a token refresh to get the latest claims
     */
  async waitForClaimsUpdate(auth, maxWaitTime = 10000) {
    const startTime = Date.now();
        
    while (Date.now() - startTime < maxWaitTime) {
      try {
        // Force token refresh
        await auth.currentUser.getIdToken(true);
                
        // Get the fresh token to check claims
        const tokenResult = await auth.currentUser.getIdTokenResult();
                
        if (tokenResult.claims.role && tokenResult.claims.claimsUpdatedAt) {
          console.log('✅ Custom claims are now available in token:', {
            role: tokenResult.claims.role,
            teamId: tokenResult.claims.teamId,
            teamName: tokenResult.claims.teamName
          });
          return tokenResult.claims;
        }
                
        // Wait a bit before checking again
        await new Promise(resolve => setTimeout(resolve, 1000));
                
      } catch (error) {
        console.warn('⚠️ Error checking token claims:', error);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
        
    throw new Error('Timeout waiting for custom claims to be applied');
  }

  /**
     * Get current user's custom claims from their token
     */
  async getCurrentUserClaims(auth) {
    try {
      if (!auth.currentUser) {
        return null;
      }
            
      const tokenResult = await auth.currentUser.getIdTokenResult();
      return {
        role: tokenResult.claims.role || null,
        teamId: tokenResult.claims.teamId || null,
        teamName: tokenResult.claims.teamName || null,
        claimsUpdatedAt: tokenResult.claims.claimsUpdatedAt || null
      };
    } catch (error) {
      console.error('❌ Error getting current user claims:', error);
      return null;
    }
  }
}

export default CustomClaimsService;
